import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

interface WaitlistStats {
  total: string
  active: string
  unsubscribed: string
  last_7_days: string
  last_30_days: string
}

interface SourceStat {
  source: string
  count: string
}

const AdminWaitlistPage: React.FC = () => {
  const navigate = useNavigate()
  const [stats, setStats] = useState<WaitlistStats | null>(null)
  const [bySource, setBySource] = useState<SourceStat[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [adminToken, setAdminToken] = useState('')
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    // Check if already authenticated
    const savedToken = localStorage.getItem('adminToken')
    if (savedToken) {
      setAdminToken(savedToken)
      setIsAuthenticated(true)
      fetchStats(savedToken)
    } else {
      setLoading(false)
    }
  }, [navigate])

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!adminToken) {
      toast.error('Please enter an admin token')
      return
    }

    // Test the token by fetching stats
    const success = await fetchStats(adminToken)
    if (success) {
      localStorage.setItem('adminToken', adminToken)
      setIsAuthenticated(true)
      toast.success('Authenticated successfully')
    }
  }

  const fetchStats = async (token: string): Promise<boolean> => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/waitlist/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('adminToken')
          setIsAuthenticated(false)
          throw new Error('Invalid admin token')
        }
        throw new Error('Failed to fetch stats')
      }

      const data = await response.json()
      setStats(data.stats)
      setBySource(data.bySource || [])
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load waitlist statistics')
      console.error(err)
      return false
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  // Show authentication form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Admin Access Required
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Enter your admin token to view waitlist statistics
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleAuth}>
            <div>
              <label htmlFor="admin-token" className="sr-only">
                Admin Token
              </label>
              <input
                id="admin-token"
                name="token"
                type="password"
                required
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Enter admin token"
                value={adminToken}
                onChange={(e) => setAdminToken(e.target.value)}
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm text-center">{error}</div>
            )}

            <div>
              <button
                type="submit"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Authenticate
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                Back to Home
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Waitlist Statistics</h1>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Total Signups</h3>
            <p className="mt-2 text-3xl font-bold text-gray-900">{stats?.total || '0'}</p>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Active</h3>
            <p className="mt-2 text-3xl font-bold text-green-600">{stats?.active || '0'}</p>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Unsubscribed</h3>
            <p className="mt-2 text-3xl font-bold text-red-600">{stats?.unsubscribed || '0'}</p>
          </div>
        </div>

        {/* Time-based Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Last 7 Days</h3>
            <p className="mt-2 text-2xl font-bold text-primary-600">{stats?.last_7_days || '0'}</p>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Last 30 Days</h3>
            <p className="mt-2 text-2xl font-bold text-primary-600">{stats?.last_30_days || '0'}</p>
          </div>
        </div>

        {/* Source Breakdown */}
        {bySource.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Signups by Source</h2>
            <div className="space-y-3">
              {bySource.map((item) => (
                <div key={item.source} className="flex justify-between items-center">
                  <span className="text-gray-600 capitalize">{item.source.replace('-', ' ')}</span>
                  <span className="font-semibold text-gray-900">{item.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Back Button */}
        <div className="mt-8">
          <button
            onClick={() => navigate('/')}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  )
}

export default AdminWaitlistPage